"use client"

import React, { FC } from "react"
import clsx from "clsx"
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { useFormContext } from "react-hook-form"
import { PostAJobModalFormValues } from "./types"
import { IconButton, Image } from "@/elements"
import { useMutation } from "@apollo/client"
import { toast } from "@/components/Toast"
import { DeleteJobPostingFileDocument } from "@/gql/graphql"
import { captureException } from "@sentry/nextjs"
import { ampli } from "@/ampli"
import { decodeGraphqlNodeId } from "@/tracking"
import { isFileValid, VALID_FILETYPES } from "@/utils/isFileValid"
interface Props {
  className?: string
  jobPostId: string
}

export const ReferenceImageInput: FC<Props> = ({ className, jobPostId }) => {
  const { setValue, watch } = useFormContext<PostAJobModalFormValues>()
  const [deleteJobPostFile] = useMutation(DeleteJobPostingFileDocument)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> & { target: { files: FileList } }) => {
    const file = e.target.files && e.target.files[0]
    if (file && file.type.startsWith("image/")) {
      if (!isFileValid({ file, withError: true })) {
        return
      }

      ampli.postAJobSetDetail({
        job_post_field: "referenceImage",
        job_id: decodeGraphqlNodeId(jobPostId).numericalId || "",
        job_post_field_value: file.name,
      })
      setValue("referenceImage", {
        file: URL.createObjectURL(file),
        name: file.name,
      })
    } else {
      toast.error("Please select an image file.")
    }
  }

  const referenceImage = watch("referenceImage")

  const handleClick = () => {
    const input = document.getElementById("fileInput")
    input?.click()
  }

  const onErrorDeleting = (existingImageUrl: string) => {
    setValue("referenceImage", {
      file: "",
      name: "",
      existingImageUrl,
    })
    toast.error("There was an error removing the cover image.")
  }

  const handleRemoveReferenceImage = async () => {
    const existingImageUrl = referenceImage?.existingImageUrl || ""
    setValue("referenceImage", {
      file: "",
      name: "",
      existingImageUrl: "",
      existingPosterId: "",
    })

    if (referenceImage?.existingImageUrl && referenceImage?.existingPosterId) {
      try {
        const response = await deleteJobPostFile({
          variables: {
            input: {
              id: referenceImage.existingPosterId!,
            },
          },
        })

        if (response.errors && response.errors.length > 0) {
          onErrorDeleting(existingImageUrl)
        }
      } catch (error) {
        captureException(error)
        onErrorDeleting(existingImageUrl)
      }
    }
  }

  const referenceImageUrl = referenceImage.file || referenceImage?.existingImageUrl

  return (
    <div className="relative size-[80px]">
      <div
        className={clsx(
          "relative size-[80px] cursor-pointer overflow-hidden rounded-md bg-bg-2 hover:bg-bg-3",
          className
        )}
        onClick={handleClick}>
        <input
          id="fileInput"
          type="file"
          className="pointer-events-none absolute inset-0 cursor-pointer opacity-0"
          onChange={handleChange}
          accept={VALID_FILETYPES.filter(type => !type.startsWith("video/")).join(",")}
        />
        {referenceImageUrl ? (
          <Image
            srcSetUrls={{
              sm: referenceImageUrl,
              md: referenceImageUrl,
              lg: referenceImageUrl,
              xl: referenceImageUrl,
            }}
            alt="Cover image"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <PlusIcon className="size-[22px] stroke-fg-1" />
          </div>
        )}
      </div>
      {referenceImageUrl && (
        <div className="absolute right-[-3px] top-[-3px]">
          <IconButton Icon={XMarkIcon} variant="primary" size="small" onClick={handleRemoveReferenceImage} />
        </div>
      )}
    </div>
  )
}
